import { useRef, useState } from 'react'
import { Eye, Download, Upload, FileCheck } from 'lucide-react'
import { certificateDownloadUrl, certificateViewUrl } from '@/api/public'
import { useAdminDates } from '@/hooks/useDates'

export type AssignedCertItem = {
  id: string
  name: string
  meta?: string | null
  completedDate: string
  certificateId: string
  uploadedAt: string
}

/**
 * Shows attendees who already have a third-party certificate on file, with view/download
 * links plus a re-upload option (replaces the file on record after a confirm prompt).
 * Sits alongside ThirdPartyCertificateUploader's "awaiting" list on the booking and
 * session detail pages.
 */
export default function AssignedThirdPartyCertificatesList({
  assigned, isLoading = false, emptyMessage, onReuploaded, reupload,
}: {
  assigned: AssignedCertItem[]
  isLoading?: boolean
  emptyMessage?: string
  onReuploaded: () => void
  reupload: (id: string, file: File) => Promise<unknown>
}) {
  const { formatDate, formatDateTime } = useAdminDates()
  const fmtDate = (d: string) => {
    return formatDate(d, { day: 'numeric', month: 'short', year: 'numeric' })
  }
  const fmtDateTime = (d: string) => {
    return formatDateTime(d, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  }

  const fileInputRef = useRef<HTMLInputElement>(null)
  const [reuploadingId, setReuploadingId] = useState<string | null>(null)
  const pendingRowRef = useRef<string | null>(null)

  function triggerReupload(id: string) {
    if (!confirm('Replace the certificate currently on file with this new file?')) return
    pendingRowRef.current = id
    fileInputRef.current?.click()
  }

  async function handleFileChosen(file: File | null) {
    const id = pendingRowRef.current
    pendingRowRef.current = null
    if (!file || !id) return
    setReuploadingId(id)
    try {
      await reupload(id, file)
      onReuploaded()
    } finally {
      setReuploadingId(null)
    }
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="px-4 py-2.5 border-b border-gray-100 bg-gray-50">
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
          Certificate on file {assigned.length > 0 && `(${assigned.length})`}
        </span>
      </div>
      {isLoading && <p className="text-sm text-gray-400 px-4 py-6 text-center">Loading…</p>}
      {!isLoading && assigned.length === 0 && (
        <p className="text-sm text-gray-400 px-4 py-6 text-center">{emptyMessage ?? 'No certificates uploaded yet.'}</p>
      )}
      {assigned.length > 0 && (
        <div className="divide-y divide-gray-100">
          {assigned.map(a => (
            <div key={a.id} className="px-4 py-2.5 flex items-center justify-between gap-3 text-sm">
              <div className="min-w-0 flex items-center gap-2">
                <FileCheck size={14} className="text-green-500 shrink-0" />
                <div className="min-w-0">
                  <span className="font-medium text-gray-900">{a.name}</span>
                  {a.meta && <span className="text-gray-400 ml-2">{a.meta}</span>}
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-xs text-gray-400" title={`Completed ${fmtDate(a.completedDate)}`}>
                  Uploaded {fmtDateTime(a.uploadedAt)}
                </span>
                <a href={certificateViewUrl(a.certificateId)} target="_blank" rel="noreferrer"
                  title="View certificate"
                  className="text-gray-400 hover:text-indigo-600 transition-colors">
                  <Eye size={15} />
                </a>
                <a href={certificateDownloadUrl(a.certificateId)} download
                  title="Download certificate"
                  className="text-gray-400 hover:text-indigo-600 transition-colors">
                  <Download size={15} />
                </a>
                <button
                  type="button"
                  onClick={() => triggerReupload(a.id)}
                  disabled={reuploadingId === a.id}
                  title="Re-upload certificate"
                  className="text-gray-400 hover:text-indigo-600 transition-colors disabled:opacity-50"
                >
                  <Upload size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={e => { const f = e.target.files?.[0] ?? null; e.target.value = ''; handleFileChosen(f) }}
      />
    </div>
  )
}
